import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Shield, Lock, Eye, AlertTriangle, ShieldCheck, UserCheck } from 'lucide-react'
import SEO from '../../components/SEO'

const ManagedSecurity = () => {
  const features = [
    {
      icon: ShieldCheck,
      title: 'Military-Grade Protection',
      description: 'Enterprise-class security architecture defending your digital assets with the most advanced threat protection available.'
    },
    {
      icon: Eye,
      title: 'Elite Monitoring',
      description: '24/7/365 threat detection and monitoring by certified security experts dedicated to your protection.'
    },
    {
      icon: AlertTriangle,
      title: 'Instant Response',
      description: 'Immediate incident response with specialized teams ready to neutralize threats before they impact you.'
    },
    {
      icon: Lock,
      title: 'Privacy Guarantee',
      description: 'Uncompromising protection of your sensitive data with zero-knowledge security architecture.'
    },
    {
      icon: UserCheck,
      title: 'Personal Security Advisor',
      description: 'Dedicated security advisor providing strategic guidance and ongoing risk assessment.'
    },
    {
      icon: Shield,
      title: 'Comprehensive Coverage',
      description: 'Protection across all your devices, networks, and properties—from homes to yachts to aircraft.'
    }
  ]

  return (
    <>
      <SEO 
        title="Managed Security - Praetorian Works"
        description="Elite managed security services for HNW and UHNW individuals. Military-grade protection, 24/7 monitoring, and dedicated security advisors."
        keywords="managed security HNW, elite cybersecurity, private security services, enterprise security for individuals"
      />
      
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-primary-900/20 via-transparent to-transparent"></div>
          
          <div className="container-max relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl mb-8 shadow-2xl">
                <Shield className="w-10 h-10 text-secondary-900" />
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Managed
                <span className="text-primary-500 ml-4">Security</span>
              </h1>
              <p className="text-xl text-neutral-300 mb-8 leading-relaxed">
                Enterprise-grade cybersecurity protecting your digital life with military-level precision. 
                Comprehensive protection across all your devices, networks, and properties with dedicated 
                expert monitoring and rapid response.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20">
          <div className="container-max">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 * index }}
                  className="bg-secondary-800 rounded-2xl p-8 border border-neutral-800 hover:border-primary-500/50 transition-all duration-300"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl mb-6 shadow-lg">
                    <feature.icon className="w-8 h-8 text-secondary-900" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-neutral-400 leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Security Approach */}
        <section className="py-20 bg-black/30">
          <div className="container-max">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
                Protection Worthy of Your <span className="text-primary-500">Status</span>
              </h2>
              <p className="text-lg text-neutral-300 mb-6 leading-relaxed">
                Your profile makes you a target. Cybercriminals specifically seek high-net-worth individuals 
                and their families. Our managed security service provides the same level of protection used 
                by governments and Fortune 500 companies, customized for your unique digital ecosystem.
              </p>
              <p className="text-lg text-neutral-300 leading-relaxed">
                From your home networks to mobile devices, from your business systems to your family's 
                personal technology—we protect it all with unwavering vigilance and expertise.
              </p>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20">
          <div className="container-max text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Secure Your Digital Empire
              </h2>
              <p className="text-xl text-neutral-300 mb-8 max-w-2xl mx-auto">
                Experience peace of mind with elite security protection tailored to your needs.
              </p>
              <Link to="/contact" className="btn-primary text-lg">
                Schedule Security Assessment
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}

export default ManagedSecurity